import { useCallback } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import type {
  ElementCreatePayload,
  ElementSize,
  ElementUpdatePayload,
  ObjectKind,
  SchemaElement,
  SchemaRegion,
  TableShape,
} from "../../../api/restaurantSchema";
import {
  useCreateElement,
  useCreateRegion,
  useDeleteElement,
  useDeleteRegion,
  useUpdateElement,
  useUpdateRegion,
} from "../../../hooks/useRestaurantSchema";
import { getApiErrorMessage } from "../../../utils/translations";
import { ROTATE_STEP } from "../../../components/restaurant-schema/elementCatalog";
import { entranceRotationAt } from "../../../components/restaurant-schema/doorGeometry";
import {
  REGION_DIMENSIONS,
  contentBounds,
  findFreeSpot,
} from "../../../components/restaurant-schema/geometry";
import { tableBlockingChange } from "../../../components/restaurant-schema/tableCollision";
import type { SchemaHistory } from "./useSchemaHistory";

export interface NewTableValues {
  name: string;
  tableShape: TableShape;
  seats: number;
  size: ElementSize;
}

const snapshotOf = (element: SchemaElement): ElementCreatePayload => ({
  regionId: element.regionId,
  kind: element.kind,
  size: element.size,
  rotation: element.rotation,
  x: element.x,
  y: element.y,
  ...(element.kind === "table"
    ? {
        name: element.name ?? "",
        tableShape: element.tableShape ?? "round",
        seats: element.seats ?? 4,
      }
    : {}),
});

type ElementDraft = Omit<ElementCreatePayload, "x" | "y">;

// Елементът, който предстои да се създаде, като псевдо-елемент — за размерите му.
const draftElement = (payload: ElementDraft): SchemaElement => ({
  id: 0,
  regionId: payload.regionId,
  kind: payload.kind,
  name: payload.name ?? null,
  tableShape: payload.tableShape ?? null,
  seats: payload.seats ?? null,
  size: payload.size,
  rotation: payload.rotation,
  x: 0,
  y: 0,
});

// Свободно място за елемента, който предстои да се създаде: до `origin`
// (средата на видимото платно) и без да застъпва съседите.
const freeSpotFor = (region: SchemaRegion, payload: ElementDraft, origin?: { x: number; y: number }) =>
  findFreeSpot(region.shape, region.elements, origin, draftElement(payload));

// Полетата, от които зависи колко място заема масата и къде.
const FOOTPRINT_FIELDS: (keyof ElementUpdatePayload)[] = ["x", "y", "rotation", "seats", "size", "tableShape"];

// Предишните стойности на точно тези полета, които промяната пипа — за „Отмени".
const previousValues = (element: SchemaElement, patch: ElementUpdatePayload) => {
  const previous: ElementUpdatePayload = {};
  for (const key of Object.keys(patch) as (keyof ElementUpdatePayload)[]) {
    Object.assign(previous, { [key]: element[key as keyof SchemaElement] });
  }
  return previous;
};

/**
 * Всички записи на редактора на едно място: заявката, стъпката за Отмени/
 * Повтори и съобщението при грешка. Местенето и промените по елемент са
 * оптимистични (useUpdateElement) и мълчат при успех — индикаторът „Запазено"
 * в лентата казва кога са стигнали до сървъра.
 */
export const useSchemaEditorActions = (
  region: SchemaRegion | undefined,
  history: SchemaHistory,
) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const createRegionMutation = useCreateRegion();
  const updateRegionMutation = useUpdateRegion();
  const deleteRegionMutation = useDeleteRegion();
  const createElementMutation = useCreateElement();
  const updateElementMutation = useUpdateElement();
  const deleteElementMutation = useDeleteElement();
  const { record, resolveId, recordAlias } = history;

  // Отказите на BE-то са изречения за четене („Маса 7 има резервация на 27.09
  // в 20:00 — първо я премести" / „… отворена сметка — първо я закрий в
  // Маркиране") — затова по-дълго от стандартните 3 s.
  const reportError = useCallback(
    (error: unknown, fallbackKey = "restaurantSchema.messages.saveError") =>
      message.error({ content: getApiErrorMessage(error, t(fallbackKey)), duration: 6 }),
    [message, t],
  );

  // ── Региони (без история — редки и потвърдени действия) ─────────────────

  const createRegion = async (name: string) => {
    try {
      const created = await createRegionMutation.mutateAsync({ name });
      message.success(t("restaurantSchema.messages.regionCreated"));
      return created;
    } catch (error) {
      reportError(error);
      throw error;
    }
  };

  const saveRegion = async (name: string) => {
    if (!region) return;
    try {
      await updateRegionMutation.mutateAsync({ id: region.id, data: { name } });
      message.success(t("restaurantSchema.messages.regionSaved"));
    } catch (error) {
      reportError(error);
      throw error;
    }
  };

  const deleteRegion = async () => {
    if (!region) return;
    try {
      await deleteRegionMutation.mutateAsync(region.id);
      message.success(t("restaurantSchema.messages.regionDeleted"));
    } catch (error) {
      reportError(error, "restaurantSchema.messages.deleteError");
      throw error;
    }
  };

  // ── Елементи (с история) ────────────────────────────────────────────────

  /** Създава и записва стъпка: Отмени = изтрий, Повтори = създай наново. */
  const createWithHistory = async (payload: ElementCreatePayload) => {
    let created: SchemaElement;
    try {
      created = await createElementMutation.mutateAsync(payload);
    } catch (error) {
      reportError(error);
      throw error;
    }
    const originalId = created.id;
    record({
      undo: async () => {
        await deleteElementMutation.mutateAsync(resolveId(originalId)).catch((error) => {
          reportError(error, "restaurantSchema.messages.deleteError");
          throw error;
        });
      },
      redo: async () => {
        const again = await createElementMutation.mutateAsync(payload).catch((error) => {
          reportError(error);
          throw error;
        });
        recordAlias(resolveId(originalId), again.id);
      },
    });
    return created;
  };

  const createTable = async (values: NewTableValues, origin?: { x: number; y: number }) => {
    if (!region) return undefined;
    const payload = { regionId: region.id, kind: "table" as const, rotation: 0, ...values };
    const created = await createWithHistory({ ...payload, ...freeSpotFor(region, payload, origin) });
    // Всяка маса се обявява — при бързо добавяне на много маси се вижда какво е станало.
    if (created) message.success(t("restaurantSchema.messages.tableCreated", { name: created.name ?? values.name }));
    return created;
  };

  /**
   * Обект от лентата: пуснат върху платното (`dropPoint` — точно там) или в
   * свободно място до `origin`. Входът, пуснат до ръба на залата (рамката на
   * останалите елементи; празен регион — самият регион), застава с гръб към
   * него — вратата се отваря навътре.
   */
  const createObject = (
    kind: ObjectKind,
    dropPoint?: { x: number; y: number },
    origin?: { x: number; y: number },
  ) => {
    if (!region) return Promise.resolve(undefined);
    const { width, height } = REGION_DIMENSIONS[region.shape];
    const room = contentBounds(region.elements) ?? { left: 0, top: 0, right: width, bottom: height };
    const rotation = kind === "entrance" && dropPoint ? entranceRotationAt(dropPoint, room) : 0;
    const payload = { regionId: region.id, kind, size: "medium" as const, rotation };
    const position = dropPoint ?? freeSpotFor(region, payload, origin);
    return createWithHistory({ ...payload, ...position });
  };

  /**
   * „Няма място — маса 5 е твърде близо…": отказаната промяна от инспектора и
   * клавишите, и завъртането с дръжката, спряно от съседна маса. Един `key` —
   * при поредно натискане на „+" съобщението се подменя, не се трупа.
   */
  const warnNoRoom = useCallback(
    (blocker: SchemaElement) => {
      message.warning({
        key: "restaurant-schema-no-room",
        content: t("restaurantSchema.messages.noRoom", { name: blocker.name ?? "" }),
      });
    },
    [message, t],
  );

  const updateElement = useCallback(
    (element: SchemaElement, patch: ElementUpdatePayload) => {
      // Маса не влиза в друга маса — нито с местене, нито със завъртане (R,
      // ±45°), нито като порасне (места, размер, форма в инспектора). Тогава
      // нищо не се праща, а съобщението казва коя маса пречи.
      const changesFootprint = FOOTPRINT_FIELDS.some((field) => field in patch);
      const staysInRegion = patch.regionId === undefined || patch.regionId === element.regionId;
      if (element.kind === "table" && changesFootprint && staysInRegion) {
        const blocker = tableBlockingChange(element, { ...element, ...patch }, region?.elements ?? []);
        if (blocker) {
          warnNoRoom(blocker);
          return;
        }
      }
      const previous = previousValues(element, patch);
      const originalId = element.id;
      const run = (data: ElementUpdatePayload) =>
        updateElementMutation
          .mutateAsync({ id: resolveId(originalId), data })
          .catch((error) => {
            reportError(error);
            throw error;
          });
      // Стъпката се записва ВЕДНАГА (редът в историята = редът на действията,
      // дори ⌘Z да дойде, докато заявката е в полет). Провалена заявка връща
      // кеша отпреди, тоест „Отмени" на нея е безвреден no-op.
      record({
        undo: () => run(previous).then(() => undefined),
        redo: () => run(patch).then(() => undefined),
      });
      run(patch).catch(() => undefined);
    },
    [region, warnNoRoom, updateElementMutation, resolveId, reportError, record],
  );

  const moveElement = useCallback(
    (elementId: number, x: number, y: number) => {
      const element = region?.elements.find((candidate) => candidate.id === elementId);
      if (element) updateElement(element, { x, y });
    },
    [region, updateElement],
  );

  const rotateElement = (element: SchemaElement, direction: 1 | -1 = 1) =>
    updateElement(element, {
      rotation: (((element.rotation + direction * ROTATE_STEP) % 360) + 360) % 360,
    });

  /** Свободното завъртане с дръжката на платното — точен ъгъл 0–359. */
  const rotateElementTo = (element: SchemaElement, rotation: number) =>
    updateElement(element, { rotation: ((Math.round(rotation) % 360) + 360) % 360 });

  // Копието стои до оригинала; копието на маса иска ново име — следващият свободен номер.
  const duplicateElement = (element: SchemaElement, nextName: string) => {
    if (!region) return Promise.resolve(undefined);
    const payload = {
      ...snapshotOf(element),
      ...(element.kind === "table" ? { name: nextName } : {}),
    };
    return createWithHistory({
      ...payload,
      ...freeSpotFor(region, payload, { x: element.x + 60, y: element.y + 60 }),
    }).catch(() => undefined);
  };

  /** Изтриване без потвърждение — „Отмени" го връща (с ново id). */
  const deleteElement = async (element: SchemaElement) => {
    const snapshot = snapshotOf(element);
    const originalId = element.id;
    try {
      await deleteElementMutation.mutateAsync(originalId);
    } catch (error) {
      reportError(error, "restaurantSchema.messages.deleteError");
      return;
    }
    record({
      undo: async () => {
        const restored = await createElementMutation.mutateAsync(snapshot).catch((error) => {
          reportError(error);
          throw error;
        });
        recordAlias(resolveId(originalId), restored.id);
      },
      redo: async () => {
        await deleteElementMutation.mutateAsync(resolveId(originalId)).catch((error) => {
          reportError(error, "restaurantSchema.messages.deleteError");
          throw error;
        });
      },
    });
  };

  return {
    createRegion,
    saveRegion,
    deleteRegion,
    createTable,
    createObject,
    updateElement,
    moveElement,
    rotateElement,
    rotateElementTo,
    warnNoRoom,
    duplicateElement,
    deleteElement,
  };
};

export type SchemaEditorActions = ReturnType<typeof useSchemaEditorActions>;
